import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CaretRight, CaretDown, Plus, Trash, Check,
  TrendUp, TrendDown, CurrencyDollar, Wallet, Shield,
  Target, Trophy, ChartLineUp, HandHeart, BookOpen,
  Lightning, Crown, Star, Eye, EyeSlash, Cross,
  ArrowRight, CaretUp, Info, Gear, Warning,
  ChatCircle, PaperPlaneTilt, Fire, Heart, HandFist,
  Confetti, Users, Sparkle, MegaphoneSimple, Moon, Sun
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── THEME HELPER ───────────────────────────────────────────
const t = (isDark, dark, light) => isDark ? dark : light;

// ─── CONSTANTS ──────────────────────────────────────────────
const DEBT_TYPES = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'medical', label: 'Medical Bills' },
  { value: 'student_loan', label: 'Student Loans' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'irs', label: 'IRS Debt' },
  { value: 'tax', label: 'Tax Debt' },
  { value: 'child_support', label: 'Child Support Arrears' },
  { value: 'collections', label: 'Collections' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'other', label: 'Other' },
];

const FAITH_SCRIPTURES = [
  { text: "The rich rule over the poor, and the borrower is slave to the lender.", ref: "Proverbs 22:7" },
  { text: "Owe no one anything, except to love each other.", ref: "Romans 13:8" },
  { text: "Honor the LORD with your wealth and with the firstfruits of all your produce.", ref: "Proverbs 3:9" },
  { text: "A good man leaves an inheritance to his children's children.", ref: "Proverbs 13:22" },
  { text: "The plans of the diligent lead surely to abundance.", ref: "Proverbs 21:5" },
];

// ─── SCORE RING COMPONENT ───────────────────────────────────
const ScoreRing = ({ score, maxScore = 850, size = 180 }) => {
  const pct = Math.min(1, score / maxScore);
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct);
  const color = score < 300 ? '#EF4444' : score < 500 ? '#F59E0B' : score < 700 ? '#10B981' : '#C4A661';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#262626" strokeWidth="8" />
        <motion.circle
          cx={size/2} cy={size/2} r={radius} fill="none"
          stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-[#F4F0E6]">{score}</span>
        <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">Freedom Score</span>
      </div>
    </div>
  );
};

// ─── THERMOMETER COMPONENT ──────────────────────────────────
const Thermometer = ({ current, total, label, color = '#10B981', icon: Icon }) => {
  const pct = total > 0 ? Math.min(100, ((total - current) / total) * 100) : 100;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#A3A3A3]" />}
          <span className="text-sm text-[#A3A3A3]">{label}</span>
        </div>
        <span className="text-sm font-bold text-[#F4F0E6]">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-3 bg-[#1A1A1A] rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
      <div className="flex justify-between text-xs text-[#666]">
        <span>${current.toLocaleString()} remaining</span>
        <span>${total.toLocaleString()} total</span>
      </div>
    </div>
  );
};

// ─── PROGRESS BAR ───────────────────────────────────────────
const ProgressBar = ({ value, max, label, color = '#10B981', suffix = '' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#A3A3A3]">{label}</span>
        <span className="text-sm font-bold text-[#F4F0E6]">${value.toLocaleString()}{suffix} <span className="text-[#666] font-normal">/ ${max.toLocaleString()}</span></span>
      </div>
      <div className="h-2.5 bg-[#1A1A1A] rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
};

// ─── STAT CARD ──────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, subtitle }) => (
  <div className="bg-[#121212] border border-[#262626] p-4 space-y-2">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <span className="text-xs text-[#A3A3A3] uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-bold text-[#F4F0E6]">{value}</p>
    {subtitle && <p className="text-xs text-[#666]">{subtitle}</p>}
  </div>
);

// ─── SNAPSHOT SETUP WIZARD ──────────────────────────────────
const SnapshotSetup = ({ onComplete, existingSnapshot, authHeader }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [income, setIncome] = useState(existingSnapshot?.income || {
    employment: 0, business: 0, freelance: 0, affiliate: 0,
    social_security: 0, retirement: 0, other: 0,
  });
  const [expenses, setExpenses] = useState(existingSnapshot?.expenses || {
    housing: 0, utilities: 0, food: 0, transportation: 0,
    insurance: 0, giving: 0, medical: 0, child_support: 0, miscellaneous: 0,
  });
  const [debts, setDebts] = useState(existingSnapshot?.debts || []);
  const [savings, setSavings] = useState(existingSnapshot?.savings || {
    emergency_fund: 0, emergency_fund_goal: 1000,
    general_savings: 0, investments: 0, retirement_savings: 0,
  });
  const [giving, setGiving] = useState(existingSnapshot?.giving || {
    tithes: 0, offerings: 0, charitable: 0, giving_goal_percent: 10,
  });

  const steps = [
    { title: 'Income', subtitle: 'Your monthly income sources', icon: TrendUp },
    { title: 'Expenses', subtitle: 'Your monthly expenses', icon: TrendDown },
    { title: 'Debts', subtitle: 'Outstanding debts', icon: Warning },
    { title: 'Savings', subtitle: 'Your savings and goals', icon: Shield },
    { title: 'Giving', subtitle: 'Generosity tracking', icon: HandHeart },
    { title: 'Review', subtitle: 'Confirm your snapshot', icon: Check },
  ];

  const addDebt = () => {
    setDebts([...debts, { id: '', name: '', type: 'other', balance: 0, min_payment: 0, interest_rate: 0 }]);
  };

  const updateDebt = (idx, field, val) => {
    const updated = [...debts];
    updated[idx] = { ...updated[idx], [field]: field === 'name' || field === 'type' ? val : parseFloat(val) || 0 };
    setDebts(updated);
  };

  const removeDebt = (idx) => setDebts(debts.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/freedom/snapshot`, {
        income, expenses, debts, savings, giving,
      }, { headers: authHeader });
      toast.success('Financial snapshot saved!');
      onComplete();
    } catch {
      toast.error('Failed to save snapshot');
    } finally {
      setSaving(false);
    }
  };

  const MoneyInput = ({ label, value, onChange }) => (
    <div className="flex items-center justify-between py-2 border-b border-[#1A1A1A]">
      <label className="text-sm text-[#A3A3A3] capitalize">{label.replace(/_/g, ' ')}</label>
      <div className="flex items-center gap-1 bg-[#1A1A1A] px-3 py-1.5">
        <span className="text-[#666] text-sm">$</span>
        <input
          type="number"
          value={value || ''}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-24 bg-transparent text-right text-[#F4F0E6] text-sm outline-none"
          placeholder="0"
          data-testid={`input-${label}`}
        />
      </div>
    </div>
  );

  const totalIncome = Object.values(income).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const totalExpenses = Object.values(expenses).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const totalDebt = debts.reduce((a, d) => a + (parseFloat(d.balance) || 0), 0);

  return (
    <div className="max-w-2xl mx-auto" data-testid="snapshot-setup">
      {/* Steps indicator */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
        {steps.map((s, i) => (
          <button
            key={i}
            onClick={() => i <= step && setStep(i)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium whitespace-nowrap transition-all ${
              i === step ? 'bg-[#C4A661] text-[#0A0A0A]' :
              i < step ? 'bg-[#1A1A1A] text-[#10B981]' : 'bg-[#121212] text-[#666]'
            }`}
            data-testid={`step-${i}`}
          >
            {i < step ? <Check size={14} /> : <s.icon size={14} />}
            <span className="hidden sm:inline">{s.title}</span>
          </button>
        ))}
      </div>

      <div className="bg-[#121212] border border-[#262626] p-6">
        <h2 className="text-xl font-bold text-[#F4F0E6] mb-1">{steps[step].title}</h2>
        <p className="text-sm text-[#A3A3A3] mb-6">{steps[step].subtitle}</p>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            {/* Step 0: Income */}
            {step === 0 && (
              <div className="space-y-1">
                {Object.entries(income).map(([key, val]) => (
                  <MoneyInput key={key} label={key} value={val} onChange={(v) => setIncome({ ...income, [key]: v })} />
                ))}
                <div className="flex justify-between pt-4 text-sm font-bold">
                  <span className="text-[#A3A3A3]">Total Monthly Income</span>
                  <span className="text-[#10B981]">${totalIncome.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Step 1: Expenses */}
            {step === 1 && (
              <div className="space-y-1">
                {Object.entries(expenses).map(([key, val]) => (
                  <MoneyInput key={key} label={key} value={val} onChange={(v) => setExpenses({ ...expenses, [key]: v })} />
                ))}
                <div className="flex justify-between pt-4 text-sm font-bold">
                  <span className="text-[#A3A3A3]">Total Monthly Expenses</span>
                  <span className="text-[#EF4444]">${totalExpenses.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* Step 2: Debts */}
            {step === 2 && (
              <div className="space-y-4">
                {debts.map((debt, idx) => (
                  <div key={idx} className="bg-[#1A1A1A] border border-[#262626] p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <input
                        value={debt.name}
                        onChange={(e) => updateDebt(idx, 'name', e.target.value)}
                        className="bg-transparent text-[#F4F0E6] text-sm font-medium outline-none flex-1"
                        placeholder="Debt name (e.g., Chase Visa)"
                        data-testid={`debt-name-${idx}`}
                      />
                      <button onClick={() => removeDebt(idx)} className="text-[#EF4444] hover:text-[#F87171] p-1">
                        <Trash size={16} />
                      </button>
                    </div>
                    <select
                      value={debt.type}
                      onChange={(e) => updateDebt(idx, 'type', e.target.value)}
                      className="w-full bg-[#121212] text-[#A3A3A3] text-sm p-2 border border-[#333] outline-none"
                      data-testid={`debt-type-${idx}`}
                    >
                      {DEBT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-[#666]">Balance</label>
                        <div className="flex items-center bg-[#121212] px-2 py-1.5 border border-[#333]">
                          <span className="text-[#666] text-xs mr-1">$</span>
                          <input type="number" value={debt.balance || ''} onChange={(e) => updateDebt(idx, 'balance', e.target.value)}
                            className="w-full bg-transparent text-[#F4F0E6] text-sm outline-none" placeholder="0"
                            data-testid={`debt-balance-${idx}`} />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-[#666]">Min Payment</label>
                        <div className="flex items-center bg-[#121212] px-2 py-1.5 border border-[#333]">
                          <span className="text-[#666] text-xs mr-1">$</span>
                          <input type="number" value={debt.min_payment || ''} onChange={(e) => updateDebt(idx, 'min_payment', e.target.value)}
                            className="w-full bg-transparent text-[#F4F0E6] text-sm outline-none" placeholder="0"
                            data-testid={`debt-payment-${idx}`} />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-[#666]">Interest %</label>
                        <div className="flex items-center bg-[#121212] px-2 py-1.5 border border-[#333]">
                          <input type="number" value={debt.interest_rate || ''} onChange={(e) => updateDebt(idx, 'interest_rate', e.target.value)}
                            className="w-full bg-transparent text-[#F4F0E6] text-sm outline-none" placeholder="0"
                            data-testid={`debt-rate-${idx}`} />
                          <span className="text-[#666] text-xs ml-1">%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={addDebt} className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-[#333] text-[#A3A3A3] hover:text-[#C4A661] hover:border-[#C4A661] transition-all" data-testid="add-debt-btn">
                  <Plus size={16} /> Add Debt
                </button>
                {debts.length > 0 && (
                  <div className="flex justify-between pt-2 text-sm font-bold">
                    <span className="text-[#A3A3A3]">Total Debt</span>
                    <span className="text-[#EF4444]">${totalDebt.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Savings */}
            {step === 3 && (
              <div className="space-y-1">
                {Object.entries(savings).map(([key, val]) => (
                  <MoneyInput key={key} label={key} value={val} onChange={(v) => setSavings({ ...savings, [key]: v })} />
                ))}
              </div>
            )}

            {/* Step 4: Giving */}
            {step === 4 && (
              <div className="space-y-1">
                {Object.entries(giving).map(([key, val]) => (
                  <MoneyInput key={key} label={key === 'giving_goal_percent' ? 'Giving Goal %' : key} value={val} onChange={(v) => setGiving({ ...giving, [key]: v })} />
                ))}
              </div>
            )}

            {/* Step 5: Review */}
            {step === 5 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#1A1A1A] p-4">
                    <p className="text-xs text-[#666] uppercase mb-1">Monthly Income</p>
                    <p className="text-xl font-bold text-[#10B981]">${totalIncome.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-4">
                    <p className="text-xs text-[#666] uppercase mb-1">Monthly Expenses</p>
                    <p className="text-xl font-bold text-[#EF4444]">${totalExpenses.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-4">
                    <p className="text-xs text-[#666] uppercase mb-1">Total Debt</p>
                    <p className="text-xl font-bold text-[#F59E0B]">${totalDebt.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#1A1A1A] p-4">
                    <p className="text-xs text-[#666] uppercase mb-1">Monthly Surplus</p>
                    <p className={`text-xl font-bold ${totalIncome - totalExpenses >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                      ${(totalIncome - totalExpenses).toLocaleString()}
                    </p>
                  </div>
                </div>
                {debts.length > 0 && (
                  <div>
                    <p className="text-xs text-[#666] uppercase mb-2">Debts ({debts.length})</p>
                    {debts.map((d, i) => (
                      <div key={i} className="flex justify-between py-1.5 border-b border-[#1A1A1A] text-sm">
                        <span className="text-[#A3A3A3]">{d.name || 'Unnamed'}</span>
                        <span className="text-[#F4F0E6]">${(d.balance || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#262626]">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#F4F0E6] disabled:opacity-30 transition-colors"
            data-testid="prev-step-btn"
          >
            <ArrowLeft size={16} /> Back
          </button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="btn-gold h-10 px-6" data-testid="next-step-btn">
              Continue <CaretRight size={16} />
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={saving} className="btn-gold h-10 px-6" data-testid="save-snapshot-btn">
              {saving ? 'Saving...' : 'Save Snapshot'} <Check size={16} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── DEBT ELIMINATION VIEW ──────────────────────────────────
const DebtElimination = ({ authHeader }) => {
  const [method, setMethod] = useState('snowball');
  const [extraPayment, setExtraPayment] = useState(0);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingDebt, setPayingDebt] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/freedom/elimination-plan?method=${method}&extra_payment=${extraPayment}`, { headers: authHeader });
      setPlan(res.data);
    } catch { toast.error('Failed to load plan'); }
    finally { setLoading(false); }
  }, [method, extraPayment, authHeader]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const handlePayment = async (debtId) => {
    const amt = parseFloat(paymentAmount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    try {
      const res = await axios.post(`${API}/api/freedom/payment`, { debt_id: debtId, amount: amt }, { headers: authHeader });
      toast.success('Payment logged!');
      if (res.data.new_milestones?.length > 0) {
        res.data.new_milestones.forEach(m => toast.success(`Milestone unlocked: ${m.title}`));
      }
      setPayingDebt(null);
      setPaymentAmount('');
      fetchPlan();
    } catch { toast.error('Failed to log payment'); }
  };

  const methods = [
    { id: 'snowball', label: 'Snowball', desc: 'Smallest balance first — quick wins to build momentum' },
    { id: 'avalanche', label: 'Avalanche', desc: 'Highest interest first — save the most money' },
    { id: 'hybrid', label: 'AI Hybrid', desc: 'Smart blend of both strategies' },
  ];

  return (
    <div className="space-y-6" data-testid="debt-elimination">
      {/* Method selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {methods.map(m => (
          <button
            key={m.id}
            onClick={() => setMethod(m.id)}
            className={`p-4 text-left border transition-all ${
              method === m.id ? 'border-[#C4A661] bg-[#C4A661]/10' : 'border-[#262626] bg-[#121212] hover:border-[#333]'
            }`}
            data-testid={`method-${m.id}`}
          >
            <p className={`text-sm font-bold ${method === m.id ? 'text-[#C4A661]' : 'text-[#F4F0E6]'}`}>{m.label}</p>
            <p className="text-xs text-[#666] mt-1">{m.desc}</p>
          </button>
        ))}
      </div>

      {/* Extra payment */}
      <div className="bg-[#121212] border border-[#262626] p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#F4F0E6] font-medium">Extra Monthly Payment</p>
            <p className="text-xs text-[#666]">Additional amount to throw at debt each month</p>
          </div>
          <div className="flex items-center gap-1 bg-[#1A1A1A] px-3 py-1.5">
            <span className="text-[#666] text-sm">$</span>
            <input
              type="number"
              value={extraPayment || ''}
              onChange={(e) => setExtraPayment(parseFloat(e.target.value) || 0)}
              className="w-24 bg-transparent text-right text-[#F4F0E6] text-sm outline-none"
              placeholder="0"
              data-testid="extra-payment-input"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A3A3A3]">Calculating plan...</div>
      ) : plan?.message ? (
        <div className="bg-[#121212] border border-[#10B981]/30 p-8 text-center">
          <Trophy size={48} className="text-[#C4A661] mx-auto mb-4" weight="fill" />
          <h3 className="text-xl font-bold text-[#F4F0E6] mb-2">You're Debt Free!</h3>
          <p className="text-[#A3A3A3]">Congratulations, King. Keep building wealth.</p>
        </div>
      ) : plan ? (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-[#121212] border border-[#262626] p-3 text-center">
              <p className="text-xs text-[#666] uppercase">Payoff Time</p>
              <p className="text-lg font-bold text-[#F4F0E6]">{plan.payoff_months} <span className="text-sm font-normal text-[#666]">mo</span></p>
            </div>
            <div className="bg-[#121212] border border-[#262626] p-3 text-center">
              <p className="text-xs text-[#666] uppercase">Total Interest</p>
              <p className="text-lg font-bold text-[#EF4444]">${plan.total_interest.toLocaleString()}</p>
            </div>
            <div className="bg-[#121212] border border-[#262626] p-3 text-center">
              <p className="text-xs text-[#666] uppercase">Min Payments</p>
              <p className="text-lg font-bold text-[#F4F0E6]">${plan.total_min_payments.toLocaleString()}<span className="text-sm font-normal text-[#666]">/mo</span></p>
            </div>
            <div className="bg-[#121212] border border-[#262626] p-3 text-center">
              <p className="text-xs text-[#666] uppercase">Extra</p>
              <p className="text-lg font-bold text-[#10B981]">+${plan.extra_payment.toLocaleString()}<span className="text-sm font-normal text-[#666]">/mo</span></p>
            </div>
          </div>

          {/* Debt list */}
          <div className="space-y-3">
            {plan.plan.map((debt, idx) => (
              <div key={debt.id} className={`bg-[#121212] border p-4 ${debt.paid_off ? 'border-[#10B981]/30' : 'border-[#262626]'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${debt.paid_off ? 'bg-[#10B981] text-white' : 'bg-[#262626] text-[#A3A3A3]'}`}>
                      {debt.paid_off ? <Check size={14} /> : idx + 1}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${debt.paid_off ? 'text-[#10B981] line-through' : 'text-[#F4F0E6]'}`}>{debt.name}</p>
                      <p className="text-xs text-[#666]">{DEBT_TYPES.find(t => t.value === debt.type)?.label || debt.type} &bull; {debt.interest_rate}% APR</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-[#F4F0E6]">${debt.original_balance.toLocaleString()}</p>
                    <p className="text-xs text-[#666]">${debt.min_payment}/mo min</p>
                  </div>
                </div>

                {/* Payment progress */}
                {!debt.paid_off && (
                  <div className="mt-3">
                    <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#C4A661] rounded-full transition-all"
                        style={{ width: `${Math.max(0, ((debt.original_balance - debt.current_balance) / debt.original_balance) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-[#666]">
                      <span>${debt.current_balance.toLocaleString()} left</span>
                      <span>{((debt.original_balance - debt.current_balance) / debt.original_balance * 100).toFixed(0)}% paid</span>
                    </div>
                  </div>
                )}

                {/* Log payment */}
                {!debt.paid_off && (
                  payingDebt === debt.id ? (
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex items-center gap-1 bg-[#1A1A1A] px-3 py-1.5 flex-1">
                        <span className="text-[#666] text-sm">$</span>
                        <input
                          type="number"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="w-full bg-transparent text-[#F4F0E6] text-sm outline-none"
                          placeholder="Payment amount"
                          autoFocus
                          data-testid={`payment-input-${debt.id}`}
                        />
                      </div>
                      <Button onClick={() => handlePayment(debt.id)} className="btn-gold h-9 px-4 text-xs" data-testid={`confirm-payment-${debt.id}`}>
                        Log
                      </Button>
                      <button onClick={() => { setPayingDebt(null); setPaymentAmount(''); }} className="text-[#666] hover:text-[#A3A3A3]">
                        <ArrowLeft size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setPayingDebt(debt.id)}
                      className="mt-3 text-xs text-[#C4A661] hover:text-[#D4B671] flex items-center gap-1 transition-colors"
                      data-testid={`pay-btn-${debt.id}`}
                    >
                      <CurrencyDollar size={14} /> Log a Payment
                    </button>
                  )
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

// ─── REACTION BUTTONS ───────────────────────────────────────
const REACTIONS = [
  { id: 'fire', emoji: 'fire', icon: Fire, color: '#F59E0B' },
  { id: 'heart', emoji: 'heart', icon: Heart, color: '#EF4444' },
  { id: 'muscle', emoji: 'muscle', icon: HandFist, color: '#10B981' },
  { id: 'pray', emoji: 'pray', icon: Cross, color: '#8B5CF6' },
  { id: 'hundred', emoji: '100', icon: Trophy, color: '#C4A661' },
];

const ReactionBar = ({ reactions = {}, onReact, userId }) => (
  <div className="flex items-center gap-1.5">
    {REACTIONS.map(r => {
      const count = reactions[r.id] || 0;
      const reacted = Array.isArray(reactions[r.id]) ? reactions[r.id] : [];
      const isActive = reacted.includes?.(userId);
      return (
        <button
          key={r.id}
          onClick={() => onReact(r.id)}
          className={`flex items-center gap-1 px-2 py-1 text-xs transition-all border ${
            isActive ? `border-[${r.color}]/40 bg-[${r.color}]/10 text-[${r.color}]` : 'border-[#262626] text-[#666] hover:text-[#A3A3A3] hover:border-[#333]'
          }`}
          data-testid={`react-${r.id}`}
        >
          <r.icon size={12} weight={isActive ? 'fill' : 'regular'} />
          <span>{typeof count === 'number' ? count : (Array.isArray(count) ? count.length : 0)}</span>
        </button>
      );
    })}
  </div>
);

// ─── AI FREEDOM COACH ───────────────────────────────────────
const AICoach = ({ authHeader, userId, faithMode }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const res = await axios.get(`${API}/api/freedom/coach/sessions`, { headers: authHeader });
        setSessions(res.data);
      } catch {}
    };
    loadSessions();
  }, [authHeader]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/api/freedom/coach/session/${sid}`, { headers: authHeader });
      setMessages(res.data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
    } catch { toast.error('Failed to load session'); }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId('');
    setShowSessions(false);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setSending(true);

    try {
      const res = await axios.post(`${API}/api/freedom/coach/chat`, {
        message: msg,
        session_id: sessionId,
      }, { headers: authHeader });

      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() }]);
      if (!sessionId) setSessionId(res.data.session_id);
    } catch {
      toast.error('Coach is unavailable right now');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const suggestions = [
    "How should I allocate my next paycheck?",
    "What debt should I attack next?",
    "How can I increase my income?",
    "Can I afford a $200 purchase right now?",
    "Help me build a monthly budget",
    "What's my path to debt freedom?",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] max-h-[700px]" data-testid="ai-coach">
      {/* Coach Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
            <Lightning size={20} className="text-[#C4A661]" weight="fill" />
            AI Freedom Coach
          </h2>
          <p className="text-sm text-[#A3A3A3]">Your personal financial advisor, powered by AI</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSessions(!showSessions)} className="px-3 py-1.5 text-xs border border-[#333] text-[#A3A3A3] hover:text-[#F4F0E6] transition-all" data-testid="show-sessions-btn">
            History
          </button>
          <button onClick={startNewSession} className="px-3 py-1.5 text-xs border border-[#C4A661]/30 text-[#C4A661] hover:bg-[#C4A661]/10 transition-all" data-testid="new-session-btn">
            <Plus size={12} className="inline mr-1" /> New Chat
          </button>
        </div>
      </div>

      {/* Session list */}
      <AnimatePresence>
        {showSessions && sessions.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
            <div className="bg-[#121212] border border-[#262626] p-3 space-y-1 max-h-40 overflow-y-auto">
              {sessions.map(s => (
                <button key={s.session_id} onClick={() => loadSession(s.session_id)} className="w-full text-left px-3 py-2 hover:bg-[#1A1A1A] transition-colors text-sm" data-testid={`session-${s.session_id}`}>
                  <span className="text-[#A3A3A3]">{s.last_message || 'Empty session'}</span>
                  <span className="text-[10px] text-[#666] ml-2">({s.message_count} msgs)</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat area */}
      <div className="flex-1 bg-[#121212] border border-[#262626] overflow-y-auto p-4 space-y-4" data-testid="coach-messages">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-16 h-16 mb-4 bg-[#C4A661]/20 flex items-center justify-center">
              <ChatCircle size={32} className="text-[#C4A661]" />
            </div>
            <h3 className="text-lg font-bold text-[#F4F0E6] mb-2">Ask your Freedom Coach</h3>
            <p className="text-sm text-[#666] max-w-md mb-6">Get personalized financial advice based on your income, debts, and goals.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(s); }}
                  className="text-left text-xs px-3 py-2 bg-[#1A1A1A] border border-[#262626] text-[#A3A3A3] hover:text-[#C4A661] hover:border-[#C4A661]/30 transition-all"
                  data-testid={`suggestion-${i}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] px-4 py-3 ${
                msg.role === 'user'
                  ? 'bg-[#C4A661]/20 text-[#F4F0E6]'
                  : 'bg-[#1A1A1A] text-[#A3A3A3]'
              }`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <Lightning size={12} className="text-[#C4A661]" weight="fill" />
                    <span className="text-[10px] font-bold text-[#C4A661] uppercase tracking-wider">Freedom Coach</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </motion.div>
          ))
        )}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-[#1A1A1A] px-4 py-3">
              <div className="flex items-center gap-2">
                <Lightning size={12} className="text-[#C4A661] animate-pulse" weight="fill" />
                <span className="text-sm text-[#666] animate-pulse">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="mt-3 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
          placeholder="Ask your Freedom Coach..."
          className="flex-1 bg-[#121212] border border-[#262626] px-4 py-3 text-sm text-[#F4F0E6] placeholder-[#666] outline-none focus:border-[#C4A661]/50 transition-colors"
          disabled={sending}
          data-testid="coach-input"
        />
        <Button onClick={sendMessage} disabled={sending || !input.trim()} className="btn-gold h-auto px-5" data-testid="coach-send-btn">
          <PaperPlaneTilt size={18} />
        </Button>
      </div>
    </div>
  );
};

// ─── COMMUNITY HUB ──────────────────────────────────────────
const POST_TYPES = [
  { value: 'general', label: 'General', color: '#A3A3A3' },
  { value: 'win', label: 'Win', color: '#10B981' },
  { value: 'encouragement', label: 'Encouragement', color: '#C4A661' },
  { value: 'question', label: 'Question', color: '#3B82F6' },
];

const CommunityHub = ({ authHeader, userId }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newType, setNewType] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/community`, { headers: authHeader });
      setPosts(res.data);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const createPost = async () => {
    if (!newContent.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/freedom/community`, { content: newContent, type: newType }, { headers: authHeader });
      toast.success('Posted!');
      setNewContent('');
      setShowNewPost(false);
      fetchPosts();
    } catch { toast.error('Failed to post'); }
    finally { setSubmitting(false); }
  };

  const reactToPost = async (postId, reaction) => {
    try {
      const res = await axios.post(`${API}/api/freedom/community/${postId}/react`, { reaction }, { headers: authHeader });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, reactions: res.data.reactions } : p));
    } catch {}
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    try {
      const res = await axios.get(`${API}/api/freedom/community/${postId}/comments`, { headers: authHeader });
      setComments(prev => ({ ...prev, [postId]: res.data }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
    } catch {}
  };

  const submitComment = async (postId) => {
    const content = newComment[postId]?.trim();
    if (!content) return;
    try {
      const res = await axios.post(`${API}/api/freedom/community/${postId}/comment`, { content }, { headers: authHeader });
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
    } catch { toast.error('Failed to comment'); }
  };

  return (
    <div data-testid="community-hub">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
            <Users size={20} className="text-[#10B981]" />
            Freedom Community
          </h2>
          <p className="text-sm text-[#A3A3A3]">Share victories, encourage brothers, grow together</p>
        </div>
        <Button onClick={() => setShowNewPost(!showNewPost)} className="btn-gold h-10 px-5" data-testid="new-community-post-btn">
          <Plus size={16} className="mr-2" /> Post
        </Button>
      </div>

      {/* New post form */}
      <AnimatePresence>
        {showNewPost && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
            <div className="bg-[#121212] border border-[#C4A661]/30 p-4 space-y-3">
              <div className="flex gap-2">
                {POST_TYPES.map(t => (
                  <button key={t.value} onClick={() => setNewType(t.value)}
                    className={`px-3 py-1 text-xs border transition-all ${newType === t.value ? `border-[${t.color}] text-[${t.color}]` : 'border-[#333] text-[#666]'}`}
                    data-testid={`post-type-${t.value}`}
                  >{t.label}</button>
                ))}
              </div>
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Share your financial journey, wins, or questions..."
                className="w-full bg-[#1A1A1A] border border-[#262626] p-3 text-sm text-[#F4F0E6] placeholder-[#666] outline-none min-h-[100px] resize-none"
                data-testid="community-post-input"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowNewPost(false)} className="px-4 py-2 text-sm text-[#666] hover:text-[#A3A3A3]">Cancel</button>
                <Button onClick={createPost} disabled={submitting || !newContent.trim()} className="btn-gold h-9 px-5 text-sm" data-testid="submit-community-post">
                  {submitting ? 'Posting...' : 'Share'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Posts */}
      {loading ? (
        <div className="text-center py-12 text-[#A3A3A3]">Loading community...</div>
      ) : posts.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-12 text-center">
          <Users size={48} className="text-[#333] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#F4F0E6] mb-2">Be the First to Share</h3>
          <p className="text-sm text-[#666]">Post your wins, questions, or encouragement for the brotherhood.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => {
            const typeInfo = POST_TYPES.find(t => t.value === post.type) || POST_TYPES[0];
            return (
              <div key={post.id} className="bg-[#121212] border border-[#262626] p-5" data-testid={`community-post-${post.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 bg-[#C4A661] flex items-center justify-center text-[#0A0A0A] font-bold text-sm shrink-0">
                    {post.author_name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#F4F0E6]">{post.author_name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 font-bold uppercase tracking-wider" style={{ color: typeInfo.color, backgroundColor: `${typeInfo.color}15` }}>{typeInfo.label}</span>
                      <span className="text-[10px] text-[#666]">{new Date(post.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mt-2 whitespace-pre-wrap">{post.content}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1A1A1A]">
                  <ReactionBar reactions={post.reactions} onReact={(r) => reactToPost(post.id, r)} userId={userId} />
                  <button onClick={() => toggleComments(post.id)} className="flex items-center gap-1 text-xs text-[#666] hover:text-[#A3A3A3] transition-colors" data-testid={`toggle-comments-${post.id}`}>
                    <ChatCircle size={14} /> {post.comment_count || 0}
                  </button>
                </div>

                {/* Comments */}
                <AnimatePresence>
                  {expandedComments[post.id] && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                      <div className="mt-3 pt-3 border-t border-[#1A1A1A] space-y-2">
                        {(comments[post.id] || []).map(c => (
                          <div key={c.id} className="flex gap-2 pl-4">
                            <div className="w-6 h-6 bg-[#262626] flex items-center justify-center text-[10px] text-[#A3A3A3] shrink-0">{c.author_name?.charAt(0) || '?'}</div>
                            <div>
                              <span className="text-xs font-bold text-[#F4F0E6]">{c.author_name}</span>
                              <p className="text-xs text-[#A3A3A3]">{c.content}</p>
                            </div>
                          </div>
                        ))}
                        <div className="flex gap-2 pl-4 mt-2">
                          <input
                            value={newComment[post.id] || ''}
                            onChange={(e) => setNewComment(prev => ({ ...prev, [post.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && submitComment(post.id)}
                            placeholder="Add encouragement..."
                            className="flex-1 bg-[#1A1A1A] border border-[#262626] px-3 py-1.5 text-xs text-[#F4F0E6] placeholder-[#666] outline-none"
                            data-testid={`comment-input-${post.id}`}
                          />
                          <button onClick={() => submitComment(post.id)} className="text-[#C4A661] hover:text-[#D4B671]">
                            <PaperPlaneTilt size={14} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── SUCCESS WALL ───────────────────────────────────────────
const WIN_TYPES = [
  { value: 'debt_payoff', label: 'Debt Payoff', icon: Target, color: '#EF4444' },
  { value: 'savings_milestone', label: 'Savings Milestone', icon: Shield, color: '#10B981' },
  { value: 'income_win', label: 'Income Win', icon: TrendUp, color: '#C4A661' },
  { value: 'business_win', label: 'Business Win', icon: Lightning, color: '#3B82F6' },
  { value: 'testimony', label: 'Testimony', icon: Star, color: '#8B5CF6' },
];

const SuccessWall = ({ authHeader, userId }) => {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewWin, setShowNewWin] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newType, setNewType] = useState('debt_payoff');
  const [newAmount, setNewAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchWins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/success-wall`, { headers: authHeader });
      setWins(res.data);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchWins(); }, [fetchWins]);

  const postWin = async () => {
    if (!newContent.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/freedom/success-wall`, {
        content: newContent, type: newType, amount: newAmount || null,
      }, { headers: authHeader });
      toast.success('Win posted to the Success Wall!');
      setNewContent('');
      setNewAmount('');
      setShowNewWin(false);
      fetchWins();
    } catch { toast.error('Failed to post'); }
    finally { setSubmitting(false); }
  };

  const reactToWin = async (winId, reaction) => {
    try {
      const res = await axios.post(`${API}/api/freedom/success-wall/${winId}/react`, { reaction }, { headers: authHeader });
      setWins(prev => prev.map(w => w.id === winId ? { ...w, reactions: res.data.reactions } : w));
    } catch {}
  };

  return (
    <div data-testid="success-wall">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
            <Trophy size={20} className="text-[#C4A661]" weight="fill" />
            Success Wall
          </h2>
          <p className="text-sm text-[#A3A3A3]">Celebrate wins and inspire the brotherhood</p>
        </div>
        <Button onClick={() => setShowNewWin(!showNewWin)} className="btn-gold h-10 px-5" data-testid="new-win-btn">
          <Confetti size={16} className="mr-2" /> Post a Win
        </Button>
      </div>

      {/* New win form */}
      <AnimatePresence>
        {showNewWin && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
            <div className="bg-[#121212] border border-[#C4A661]/30 p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {WIN_TYPES.map(t => (
                  <button key={t.value} onClick={() => setNewType(t.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border transition-all ${
                      newType === t.value ? `border-[#C4A661] text-[#C4A661] bg-[#C4A661]/10` : 'border-[#333] text-[#666] hover:text-[#A3A3A3]'
                    }`}
                    data-testid={`win-type-${t.value}`}
                  >
                    <t.icon size={12} /> {t.label}
                  </button>
                ))}
              </div>
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Share your win! E.g., 'I paid off my first credit card today!'"
                className="w-full bg-[#1A1A1A] border border-[#262626] p-3 text-sm text-[#F4F0E6] placeholder-[#666] outline-none min-h-[80px] resize-none"
                data-testid="win-content-input"
              />
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-[#1A1A1A] border border-[#262626] px-3 py-1.5">
                  <span className="text-[#666] text-sm">$</span>
                  <input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)}
                    className="w-24 bg-transparent text-[#F4F0E6] text-sm outline-none" placeholder="Amount (optional)"
                    data-testid="win-amount-input" />
                </div>
                <div className="flex-1" />
                <button onClick={() => setShowNewWin(false)} className="px-4 py-2 text-sm text-[#666]">Cancel</button>
                <Button onClick={postWin} disabled={submitting || !newContent.trim()} className="btn-gold h-9 px-5 text-sm" data-testid="submit-win-btn">
                  {submitting ? 'Posting...' : 'Celebrate'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wins list */}
      {loading ? (
        <div className="text-center py-12 text-[#A3A3A3]">Loading success wall...</div>
      ) : wins.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-12 text-center">
          <Trophy size={48} className="text-[#333] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#F4F0E6] mb-2">No Wins Yet</h3>
          <p className="text-sm text-[#666]">Be the first to celebrate a financial victory!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {wins.map(win => {
            const typeInfo = WIN_TYPES.find(t => t.value === win.type) || WIN_TYPES[4];
            return (
              <motion.div
                key={win.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-[#121212] border border-[#262626] p-5 hover:border-[#333] transition-colors"
                data-testid={`win-${win.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: `${typeInfo.color}20` }}>
                    <typeInfo.icon size={20} style={{ color: typeInfo.color }} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-[#F4F0E6]">{win.author_name}</span>
                      <span className="text-[10px] text-[#666]">{new Date(win.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-[#A3A3A3] mb-2 whitespace-pre-wrap">{win.content}</p>
                    {win.amount && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#10B981]/10 text-[#10B981] text-xs font-bold mb-2">
                        <CurrencyDollar size={12} /> ${Number(win.amount).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#1A1A1A]">
                  <ReactionBar reactions={win.reactions} onReact={(r) => reactToWin(win.id, r)} userId={userId} />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── FAITH MODE EXPANDED ────────────────────────────────────
const FaithContent = ({ authHeader }) => {
  const [faithData, setFaithData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/freedom/faith/daily`, { headers: authHeader });
        setFaithData(res.data);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [authHeader]);

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading faith content...</div>;
  if (!faithData) return null;

  return (
    <div className="space-y-6" data-testid="faith-content">
      {/* Daily Scripture */}
      <div className="bg-gradient-to-br from-[#121212] to-[#1A1A1A] border border-[#C4A661]/30 p-8">
        <div className="flex items-center gap-2 mb-4">
          <Cross size={18} className="text-[#C4A661]" weight="fill" />
          <span className="text-xs font-bold text-[#C4A661] uppercase tracking-wider">Today's Scripture</span>
        </div>
        <blockquote className="text-lg italic text-[#F4F0E6] mb-3 font-serif leading-relaxed">
          &ldquo;{faithData.scripture?.text}&rdquo;
        </blockquote>
        <p className="text-sm text-[#C4A661] font-medium">&mdash; {faithData.scripture?.ref}</p>
        <div className="mt-4 pt-4 border-t border-[#C4A661]/20">
          <p className="text-sm text-[#A3A3A3]">
            <span className="text-[#C4A661] font-bold">Stewardship Principle:</span> {faithData.scripture?.principle}
          </p>
        </div>
      </div>

      {/* Stewardship Tip */}
      <div className="bg-[#121212] border border-[#262626] p-6">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={18} className="text-[#10B981]" />
          <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">Daily Stewardship Tip</span>
        </div>
        <p className="text-sm text-[#F4F0E6]">{faithData.stewardship_tip}</p>
      </div>

      {/* Encouragement */}
      <div className="bg-[#121212] border border-[#262626] p-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkle size={18} className="text-[#8B5CF6]" />
          <span className="text-xs font-bold text-[#8B5CF6] uppercase tracking-wider">Personal Encouragement</span>
        </div>
        <p className="text-sm text-[#F4F0E6] italic">{faithData.encouragement}</p>
      </div>
    </div>
  );
};

// ─── CHALLENGES COMPONENT ───────────────────────────────────
const DIFFICULTY_COLORS = { easy: '#10B981', medium: '#F59E0B', hard: '#EF4444' };

const Challenges = ({ authHeader, userId }) => {
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [logAmount, setLogAmount] = useState('');
  const [logNote, setLogNote] = useState('');
  const [leaderboard, setLeaderboard] = useState({});

  const fetchChallenges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/challenges`, { headers: authHeader });
      setChallenges(res.data);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const joinChallenge = async (id) => {
    try {
      await axios.post(`${API}/api/freedom/challenges/${id}/join`, {}, { headers: authHeader });
      toast.success('Challenge accepted!');
      fetchChallenges();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to join'); }
  };

  const leaveChallenge = async (id) => {
    try {
      await axios.delete(`${API}/api/freedom/challenges/${id}/leave`, { headers: authHeader });
      toast.success('Left challenge');
      fetchChallenges();
    } catch { toast.error('Failed to leave'); }
  };

  const logProgress = async (id) => {
    const amt = parseFloat(logAmount) || 1;
    try {
      const res = await axios.post(`${API}/api/freedom/challenges/${id}/log`, { amount: amt, note: logNote }, { headers: authHeader });
      if (res.data.completed) toast.success('Challenge completed!');
      else toast.success('Progress logged!');
      if (res.data.new_badges?.length) res.data.new_badges.forEach(b => toast.success(`Badge earned: ${b.title}`));
      setLogAmount('');
      setLogNote('');
      fetchChallenges();
    } catch { toast.error('Failed to log'); }
  };

  const fetchLeaderboard = async (id) => {
    try {
      const res = await axios.get(`${API}/api/freedom/challenges/${id}/leaderboard`, { headers: authHeader });
      setLeaderboard(prev => ({ ...prev, [id]: res.data }));
    } catch {}
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    fetchLeaderboard(id);
  };

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading challenges...</div>;

  return (
    <div data-testid="challenges">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
          <Target size={20} className="text-[#F59E0B]" weight="fill" />
          Freedom Challenges
        </h2>
        <p className="text-sm text-[#A3A3A3]">Push yourself with community-backed financial challenges</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {challenges.map(ch => {
          const pct = ch.goal_value > 0 ? Math.min(100, (ch.progress / ch.goal_value) * 100) : 0;
          return (
            <div key={ch.id} className={`bg-[#121212] border p-5 transition-all ${ch.completed ? 'border-[#10B981]/40' : ch.joined ? 'border-[#C4A661]/30' : 'border-[#262626]'}`} data-testid={`challenge-${ch.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ backgroundColor: `${ch.color}20` }}>
                  <Lightning size={20} style={{ color: ch.color }} weight="fill" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-[#F4F0E6]">{ch.title}</span>
                    <span className="text-[10px] px-1.5 py-0.5 font-bold uppercase" style={{ color: DIFFICULTY_COLORS[ch.difficulty], backgroundColor: `${DIFFICULTY_COLORS[ch.difficulty]}15` }}>{ch.difficulty}</span>
                    {ch.completed && <span className="text-[10px] px-1.5 py-0.5 bg-[#10B981]/20 text-[#10B981] font-bold uppercase">Complete</span>}
                  </div>
                  <p className="text-xs text-[#666] mt-1">{ch.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-[#666]">
                    <span>{ch.duration_days} days</span>
                    <span>{ch.points} pts</span>
                    <span>{ch.participant_count} joined</span>
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              {ch.joined && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#A3A3A3]">Progress</span>
                    <span className="text-[#F4F0E6] font-bold">{ch.progress}/{ch.goal_value}</span>
                  </div>
                  <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: ch.completed ? '#10B981' : ch.color }} />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {!ch.joined ? (
                  <Button onClick={() => joinChallenge(ch.id)} className="btn-gold h-8 px-4 text-xs" data-testid={`join-${ch.id}`}>
                    Join Challenge
                  </Button>
                ) : ch.completed ? (
                  <span className="flex items-center gap-1 text-xs text-[#10B981]"><Check size={14} /> Completed</span>
                ) : (
                  <>
                    <button onClick={() => toggleExpand(ch.id)} className="text-xs text-[#C4A661] hover:text-[#D4B671]" data-testid={`expand-${ch.id}`}>
                      {expandedId === ch.id ? 'Hide' : 'Log Progress'}
                    </button>
                    <button onClick={() => leaveChallenge(ch.id)} className="text-xs text-[#666] hover:text-[#EF4444] ml-auto">Leave</button>
                  </>
                )}
              </div>

              {/* Expanded: log + leaderboard */}
              <AnimatePresence>
                {expandedId === ch.id && ch.joined && !ch.completed && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="mt-4 pt-4 border-t border-[#1A1A1A] space-y-3">
                      <div className="flex gap-2">
                        {ch.goal_type === 'amount' && (
                          <div className="flex items-center bg-[#1A1A1A] px-2 py-1.5 border border-[#262626]">
                            <span className="text-[#666] text-xs mr-1">$</span>
                            <input type="number" value={logAmount} onChange={(e) => setLogAmount(e.target.value)} className="w-20 bg-transparent text-sm text-[#F4F0E6] outline-none" placeholder="0" data-testid={`log-amount-${ch.id}`} />
                          </div>
                        )}
                        <input value={logNote} onChange={(e) => setLogNote(e.target.value)} placeholder="Note (optional)" className="flex-1 bg-[#1A1A1A] border border-[#262626] px-3 py-1.5 text-xs text-[#F4F0E6] outline-none" data-testid={`log-note-${ch.id}`} />
                        <Button onClick={() => logProgress(ch.id)} className="btn-gold h-8 px-3 text-xs" data-testid={`log-btn-${ch.id}`}>Log</Button>
                      </div>
                      {ch.tips?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider mb-1">Tips</p>
                          {ch.tips.map((t, i) => <p key={i} className="text-xs text-[#A3A3A3]">- {t}</p>)}
                        </div>
                      )}
                      {leaderboard[ch.id]?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-[#666] uppercase tracking-wider mb-1">Leaderboard</p>
                          {leaderboard[ch.id].map(l => (
                            <div key={l.rank} className={`flex items-center justify-between py-1 text-xs ${l.is_you ? 'text-[#C4A661]' : 'text-[#A3A3A3]'}`}>
                              <span>#{l.rank} {l.name} {l.is_you ? '(You)' : ''}</span>
                              <span className="font-bold">{l.progress}/{ch.goal_value} {l.completed ? '✓' : ''}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── BADGES COMPONENT ───────────────────────────────────────
const BadgesView = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/api/freedom/badges`, { headers: authHeader });
        setData(res.data);
        if (res.data.new_badges?.length) res.data.new_badges.forEach(b => toast.success(`New badge: ${b.title}`));
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [authHeader]);

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading badges...</div>;
  if (!data) return null;

  const categories = [...new Set(data.badges.map(b => b.category))];

  return (
    <div data-testid="badges-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
            <Crown size={20} className="text-[#C4A661]" weight="fill" />
            Badges & Achievements
          </h2>
          <p className="text-sm text-[#A3A3A3]">{data.earned_count}/{data.total_count} earned &bull; {data.total_points} points</p>
        </div>
      </div>

      {categories.map(cat => (
        <div key={cat} className="mb-6">
          <h3 className="text-xs font-bold text-[#666] uppercase tracking-wider mb-3 capitalize">{cat}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {data.badges.filter(b => b.category === cat).map(badge => (
              <div
                key={badge.id}
                className={`p-4 text-center border transition-all ${
                  badge.earned ? 'bg-[#121212] border-[#C4A661]/30' : 'bg-[#0A0A0A] border-[#1A1A1A] opacity-40'
                }`}
                data-testid={`badge-${badge.id}`}
              >
                <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: badge.earned ? `${badge.color}20` : '#1A1A1A' }}>
                  <Star size={24} style={{ color: badge.earned ? badge.color : '#333' }} weight={badge.earned ? 'fill' : 'regular'} />
                </div>
                <p className="text-xs font-bold text-[#F4F0E6] mb-0.5">{badge.title}</p>
                <p className="text-[10px] text-[#666]">{badge.description}</p>
                {badge.earned && badge.earned_at && (
                  <p className="text-[9px] text-[#C4A661] mt-1">{new Date(badge.earned_at).toLocaleDateString()}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── INCOME EXPANSION CENTER ────────────────────────────────
const INCOME_TYPES = [
  { value: 'affiliate', label: 'Affiliate Income' },
  { value: 'side_hustle', label: 'Side Hustle' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'saas', label: 'SaaS Revenue' },
  { value: 'freelancing', label: 'Freelancing' },
  { value: 'investments', label: 'Investments' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'other', label: 'Other' },
];

const IncomeCenter = ({ authHeader }) => {
  const [sources, setSources] = useState([]);
  const [totalMonthly, setTotalMonthly] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('side_hustle');
  const [newAmount, setNewAmount] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  const fetchSources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/income-sources`, { headers: authHeader });
      setSources(res.data.sources);
      setTotalMonthly(res.data.total_monthly);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  const addSource = async () => {
    if (!newName.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/freedom/income-sources`, { name: newName, type: newType, amount: parseFloat(newAmount) || 0, notes: newNotes }, { headers: authHeader });
      toast.success('Income source added!');
      setNewName(''); setNewAmount(''); setNewNotes('');
      setShowAdd(false);
      fetchSources();
    } catch { toast.error('Failed to add'); }
    finally { setSubmitting(false); }
  };

  const updateAmount = async (id) => {
    try {
      await axios.put(`${API}/api/freedom/income-sources/${id}`, { amount: parseFloat(editAmount) || 0 }, { headers: authHeader });
      toast.success('Updated');
      setEditId(null);
      fetchSources();
    } catch { toast.error('Failed to update'); }
  };

  const deleteSource = async (id) => {
    try {
      await axios.delete(`${API}/api/freedom/income-sources/${id}`, { headers: authHeader });
      toast.success('Removed');
      fetchSources();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading income sources...</div>;

  return (
    <div data-testid="income-center">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
            <TrendUp size={20} className="text-[#10B981]" />
            Income Expansion Center
          </h2>
          <p className="text-sm text-[#A3A3A3]">Track and grow your income streams</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} className="btn-gold h-10 px-5" data-testid="add-income-btn">
          <Plus size={16} className="mr-2" /> Add Source
        </Button>
      </div>

      {/* Total */}
      <div className="bg-gradient-to-r from-[#10B981]/10 to-transparent border border-[#10B981]/20 p-6 mb-6">
        <p className="text-xs text-[#10B981] uppercase tracking-wider mb-1">Total Extra Monthly Income</p>
        <p className="text-3xl font-bold text-[#F4F0E6]">${totalMonthly.toLocaleString()}<span className="text-sm font-normal text-[#666]">/mo</span></p>
        <p className="text-xs text-[#666] mt-1">{sources.length} active income stream{sources.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Add form */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
            <div className="bg-[#121212] border border-[#C4A661]/30 p-4 space-y-3">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Income source name" className="w-full bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="income-name-input" />
              <div className="flex gap-3">
                <select value={newType} onChange={(e) => setNewType(e.target.value)} className="bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#A3A3A3] outline-none flex-1" data-testid="income-type-select">
                  {INCOME_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <div className="flex items-center bg-[#1A1A1A] border border-[#262626] px-3 py-2">
                  <span className="text-[#666] text-sm mr-1">$</span>
                  <input type="number" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} className="w-24 bg-transparent text-sm text-[#F4F0E6] outline-none" placeholder="Monthly" data-testid="income-amount-input" />
                </div>
              </div>
              <input value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Notes (optional)" className="w-full bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="income-notes-input" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-[#666]">Cancel</button>
                <Button onClick={addSource} disabled={submitting || !newName.trim()} className="btn-gold h-9 px-5 text-sm" data-testid="submit-income-btn">{submitting ? 'Adding...' : 'Add'}</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sources list */}
      {sources.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-12 text-center">
          <TrendUp size={48} className="text-[#333] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#F4F0E6] mb-2">No Income Sources Yet</h3>
          <p className="text-sm text-[#666]">Start tracking your side hustles, freelance work, and extra income.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map(src => (
            <div key={src.id} className="bg-[#121212] border border-[#262626] p-4 flex items-center gap-4" data-testid={`income-${src.id}`}>
              <div className="w-10 h-10 bg-[#10B981]/10 flex items-center justify-center shrink-0">
                <CurrencyDollar size={20} className="text-[#10B981]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#F4F0E6]">{src.name}</p>
                <p className="text-xs text-[#666]">{INCOME_TYPES.find(t => t.value === src.type)?.label || src.type}{src.notes ? ` — ${src.notes}` : ''}</p>
              </div>
              <div className="text-right shrink-0">
                {editId === src.id ? (
                  <div className="flex items-center gap-1">
                    <div className="flex items-center bg-[#1A1A1A] px-2 py-1 border border-[#262626]">
                      <span className="text-[#666] text-xs mr-1">$</span>
                      <input type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} className="w-20 bg-transparent text-sm text-[#F4F0E6] outline-none" autoFocus data-testid={`edit-amount-${src.id}`} />
                    </div>
                    <button onClick={() => updateAmount(src.id)} className="text-[#10B981] p-1"><Check size={14} /></button>
                    <button onClick={() => setEditId(null)} className="text-[#666] p-1"><ArrowLeft size={14} /></button>
                  </div>
                ) : (
                  <button onClick={() => { setEditId(src.id); setEditAmount(src.amount.toString()); }} className="text-lg font-bold text-[#10B981]" data-testid={`amount-${src.id}`}>
                    ${src.amount.toLocaleString()}<span className="text-xs font-normal text-[#666]">/mo</span>
                  </button>
                )}
              </div>
              <button onClick={() => deleteSource(src.id)} className="text-[#666] hover:text-[#EF4444] p-1 shrink-0" data-testid={`delete-income-${src.id}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── ACCOUNTABILITY GROUPS ──────────────────────────────────
const GROUP_CATEGORIES = [
  { value: 'debt_free', label: 'Debt Freedom' },
  { value: 'entrepreneurs', label: 'Entrepreneurs' },
  { value: 'retirement', label: 'Retirement Builders' },
  { value: 'side_hustle', label: 'Side Hustle Growth' },
  { value: 'medical_debt', label: 'Medical Debt Recovery' },
  { value: 'general', label: 'General' },
];

const AccountabilityGroups = ({ authHeader, userId }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupDetail, setGroupDetail] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinData, setCheckinData] = useState({ wins: '', struggles: '', goals_next_week: '' });
  const [form, setForm] = useState({ name: '', description: '', category: 'general', goal: '' });

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/groups`, { headers: authHeader });
      setGroups(res.data);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const createGroup = async () => {
    if (!form.name.trim()) return;
    try {
      await axios.post(`${API}/api/freedom/groups`, form, { headers: authHeader });
      toast.success('Group created!');
      setShowCreate(false);
      setForm({ name: '', description: '', category: 'general', goal: '' });
      fetchGroups();
    } catch { toast.error('Failed to create'); }
  };

  const joinGroup = async (id) => {
    try {
      await axios.post(`${API}/api/freedom/groups/${id}/join`, {}, { headers: authHeader });
      toast.success('Joined!');
      fetchGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const leaveGroup = async (id) => {
    try {
      await axios.delete(`${API}/api/freedom/groups/${id}/leave`, { headers: authHeader });
      toast.success('Left group');
      setActiveGroup(null);
      setGroupDetail(null);
      fetchGroups();
    } catch { toast.error('Failed'); }
  };

  const openGroup = async (id) => {
    try {
      const res = await axios.get(`${API}/api/freedom/groups/${id}`, { headers: authHeader });
      setGroupDetail(res.data);
      setActiveGroup(id);
    } catch { toast.error('Failed to load group'); }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeGroup) return;
    try {
      await axios.post(`${API}/api/freedom/groups/${activeGroup}/message`, { content: newMsg }, { headers: authHeader });
      setNewMsg('');
      openGroup(activeGroup);
    } catch { toast.error('Failed'); }
  };

  const submitCheckin = async () => {
    if (!activeGroup) return;
    try {
      await axios.post(`${API}/api/freedom/groups/${activeGroup}/checkin`, checkinData, { headers: authHeader });
      toast.success('Check-in submitted!');
      setShowCheckin(false);
      setCheckinData({ wins: '', struggles: '', goals_next_week: '' });
      openGroup(activeGroup);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading groups...</div>;

  // Group detail view
  if (activeGroup && groupDetail) {
    return (
      <div data-testid="group-detail">
        <button onClick={() => { setActiveGroup(null); setGroupDetail(null); }} className="flex items-center gap-1 text-sm text-[#A3A3A3] hover:text-[#F4F0E6] mb-4" data-testid="back-to-groups">
          <ArrowLeft size={16} /> Back to Groups
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-[#F4F0E6]">{groupDetail.name}</h2>
            <p className="text-sm text-[#A3A3A3]">{groupDetail.description || groupDetail.goal}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCheckin(!showCheckin)} className="px-3 py-1.5 text-xs border border-[#C4A661]/30 text-[#C4A661] hover:bg-[#C4A661]/10" data-testid="checkin-btn">Check-in</button>
            <button onClick={() => leaveGroup(activeGroup)} className="px-3 py-1.5 text-xs border border-[#333] text-[#666] hover:text-[#EF4444]">Leave</button>
          </div>
        </div>

        {/* Check-in form */}
        <AnimatePresence>
          {showCheckin && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-4">
              <div className="bg-[#121212] border border-[#C4A661]/30 p-4 space-y-3">
                <h3 className="text-sm font-bold text-[#C4A661]">Weekly Check-in</h3>
                <div><label className="text-xs text-[#666]">Wins this week</label><textarea value={checkinData.wins} onChange={(e) => setCheckinData(p => ({ ...p, wins: e.target.value }))} className="w-full bg-[#1A1A1A] border border-[#262626] p-2 text-sm text-[#F4F0E6] outline-none mt-1 min-h-[60px] resize-none" data-testid="checkin-wins" /></div>
                <div><label className="text-xs text-[#666]">Struggles</label><textarea value={checkinData.struggles} onChange={(e) => setCheckinData(p => ({ ...p, struggles: e.target.value }))} className="w-full bg-[#1A1A1A] border border-[#262626] p-2 text-sm text-[#F4F0E6] outline-none mt-1 min-h-[60px] resize-none" data-testid="checkin-struggles" /></div>
                <div><label className="text-xs text-[#666]">Goals for next week</label><textarea value={checkinData.goals_next_week} onChange={(e) => setCheckinData(p => ({ ...p, goals_next_week: e.target.value }))} className="w-full bg-[#1A1A1A] border border-[#262626] p-2 text-sm text-[#F4F0E6] outline-none mt-1 min-h-[60px] resize-none" data-testid="checkin-goals" /></div>
                <Button onClick={submitCheckin} className="btn-gold h-9 px-5 text-sm" data-testid="submit-checkin">Submit Check-in</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Members & Leaderboard */}
          <div className="bg-[#121212] border border-[#262626] p-4">
            <h3 className="text-xs font-bold text-[#666] uppercase tracking-wider mb-3">Members ({groupDetail.members?.length})</h3>
            <div className="space-y-2">
              {groupDetail.members?.map((m, i) => (
                <div key={m.user_id} className={`flex items-center justify-between py-1.5 text-sm ${m.is_you ? 'text-[#C4A661]' : 'text-[#A3A3A3]'}`}>
                  <span>#{i + 1} {m.name} {m.is_you ? '(You)' : ''} {m.role === 'admin' ? '★' : ''}</span>
                  <span className="text-xs font-bold">{m.score}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Chat */}
          <div className="lg:col-span-2 bg-[#121212] border border-[#262626] flex flex-col" style={{ maxHeight: '500px' }}>
            <div className="p-3 border-b border-[#1A1A1A]"><span className="text-xs font-bold text-[#666] uppercase tracking-wider">Group Chat</span></div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="group-chat">
              {groupDetail.messages?.length === 0 && <p className="text-center text-sm text-[#666] py-8">No messages yet. Start the conversation!</p>}
              {groupDetail.messages?.map(msg => (
                <div key={msg.id} className={`flex gap-2 ${msg.author_id === userId ? 'flex-row-reverse' : ''}`}>
                  <div className={`max-w-[75%] px-3 py-2 text-sm ${msg.author_id === userId ? 'bg-[#C4A661]/20 text-[#F4F0E6]' : 'bg-[#1A1A1A] text-[#A3A3A3]'}`}>
                    <span className="text-[10px] font-bold text-[#666] block mb-0.5">{msg.author_name}</span>
                    {msg.content}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-[#1A1A1A] flex gap-2">
              <input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMessage()} placeholder="Message the group..." className="flex-1 bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="group-msg-input" />
              <Button onClick={sendMessage} disabled={!newMsg.trim()} className="btn-gold h-auto px-4" data-testid="group-msg-send"><PaperPlaneTilt size={16} /></Button>
            </div>
          </div>
        </div>

        {/* Recent Check-ins */}
        {groupDetail.checkins?.length > 0 && (
          <div className="mt-4 bg-[#121212] border border-[#262626] p-4">
            <h3 className="text-xs font-bold text-[#666] uppercase tracking-wider mb-3">Recent Check-ins</h3>
            <div className="space-y-3">
              {groupDetail.checkins.map(c => (
                <div key={c.id} className="border-b border-[#1A1A1A] pb-3 last:border-0">
                  <div className="flex items-center gap-2 mb-1"><span className="text-xs font-bold text-[#F4F0E6]">{c.author_name}</span><span className="text-[10px] text-[#666]">{new Date(c.created_at).toLocaleDateString()}</span></div>
                  {c.wins && <p className="text-xs text-[#10B981]"><span className="font-bold">Wins:</span> {c.wins}</p>}
                  {c.struggles && <p className="text-xs text-[#EF4444]"><span className="font-bold">Struggles:</span> {c.struggles}</p>}
                  {c.goals_next_week && <p className="text-xs text-[#C4A661]"><span className="font-bold">Goals:</span> {c.goals_next_week}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Groups list view
  return (
    <div data-testid="groups-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2"><Users size={20} className="text-[#8B5CF6]" /> Accountability Groups</h2>
          <p className="text-sm text-[#A3A3A3]">Join a brotherhood group for accountability and support</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="btn-gold h-10 px-5" data-testid="create-group-btn"><Plus size={16} className="mr-2" /> Create Group</Button>
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
            <div className="bg-[#121212] border border-[#C4A661]/30 p-4 space-y-3">
              <input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Group name" className="w-full bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="group-name-input" />
              <input value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" className="w-full bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="group-desc-input" />
              <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))} className="bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#A3A3A3] outline-none w-full" data-testid="group-category-select">
                {GROUP_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input value={form.goal} onChange={(e) => setForm(p => ({ ...p, goal: e.target.value }))} placeholder="Group goal (e.g., Debt free by 2027)" className="w-full bg-[#1A1A1A] border border-[#262626] px-3 py-2 text-sm text-[#F4F0E6] placeholder-[#666] outline-none" data-testid="group-goal-input" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#666]">Cancel</button>
                <Button onClick={createGroup} disabled={!form.name.trim()} className="btn-gold h-9 px-5 text-sm" data-testid="submit-group-btn">Create</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {groups.length === 0 ? (
        <div className="bg-[#121212] border border-[#262626] p-12 text-center">
          <Users size={48} className="text-[#333] mx-auto mb-4" />
          <h3 className="text-lg font-bold text-[#F4F0E6] mb-2">No Groups Yet</h3>
          <p className="text-sm text-[#666]">Create the first accountability group for the brotherhood.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {groups.map(g => (
            <div key={g.id} className="bg-[#121212] border border-[#262626] p-5 hover:border-[#333] transition-all" data-testid={`group-${g.id}`}>
              <h3 className="text-sm font-bold text-[#F4F0E6] mb-1">{g.name}</h3>
              <p className="text-xs text-[#666] mb-2">{g.description || g.goal}</p>
              <div className="flex items-center gap-3 text-[10px] text-[#666] mb-3">
                <span>{GROUP_CATEGORIES.find(c => c.value === g.category)?.label}</span>
                <span>{g.member_count}/{g.max_members} members</span>
                <span>by {g.creator_name}</span>
              </div>
              {g.is_member ? (
                <Button onClick={() => openGroup(g.id)} className="btn-gold h-8 px-4 text-xs" data-testid={`open-group-${g.id}`}>Open Group</Button>
              ) : (
                <Button onClick={() => joinGroup(g.id)} className="h-8 px-4 text-xs border border-[#C4A661] text-[#C4A661] bg-transparent hover:bg-[#C4A661]/10" data-testid={`join-group-${g.id}`}>Join</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── LEARNING CENTER ────────────────────────────────────────
const LearningCenter = ({ authHeader }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [activeLesson, setActiveLesson] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  const fetchModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/freedom/learning`, { headers: authHeader });
      setModules(res.data);
    } catch {} finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const openModule = async (id) => {
    try {
      const res = await axios.get(`${API}/api/freedom/learning/${id}`, { headers: authHeader });
      setModuleData(res.data);
      setActiveModule(id);
      setActiveLesson(0);
      setShowQuiz(false);
      setQuizResult(null);
      setQuizAnswers(new Array(res.data.quiz?.length || 0).fill(-1));
    } catch { toast.error('Failed to load module'); }
  };

  const completeLesson = async (lessonId) => {
    try {
      await axios.post(`${API}/api/freedom/learning/${activeModule}/complete-lesson`, { lesson_id: lessonId }, { headers: authHeader });
      const res = await axios.get(`${API}/api/freedom/learning/${activeModule}`, { headers: authHeader });
      setModuleData(res.data);
      if (activeLesson < (moduleData?.lessons?.length || 0) - 1) setActiveLesson(activeLesson + 1);
      else setShowQuiz(true);
      toast.success('Lesson complete!');
    } catch { toast.error('Failed'); }
  };

  const submitQuiz = async () => {
    try {
      const res = await axios.post(`${API}/api/freedom/learning/${activeModule}/quiz`, { answers: quizAnswers }, { headers: authHeader });
      setQuizResult(res.data);
      fetchModules();
      if (res.data.passed) toast.success(`Quiz passed! ${res.data.score}/${res.data.total}`);
      else toast.error(`${res.data.score}/${res.data.total} — Try again!`);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="text-center py-12 text-[#A3A3A3]">Loading courses...</div>;

  // Module detail view
  if (activeModule && moduleData) {
    const lesson = moduleData.lessons?.[activeLesson];
    const isCompleted = moduleData.completed_lessons?.includes(lesson?.id);

    return (
      <div data-testid="module-detail">
        <button onClick={() => { setActiveModule(null); setModuleData(null); }} className="flex items-center gap-1 text-sm text-[#A3A3A3] hover:text-[#F4F0E6] mb-4" data-testid="back-to-modules"><ArrowLeft size={16} /> Back to Courses</button>

        <h2 className="text-xl font-bold text-[#F4F0E6] mb-1">{moduleData.title}</h2>
        <p className="text-sm text-[#A3A3A3] mb-6">{moduleData.description}</p>

        {/* Lesson navigation */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2">
          {moduleData.lessons?.map((l, i) => {
            const done = moduleData.completed_lessons?.includes(l.id);
            return (
              <button key={l.id} onClick={() => { setActiveLesson(i); setShowQuiz(false); }}
                className={`px-3 py-2 text-xs font-medium whitespace-nowrap border transition-all ${
                  i === activeLesson && !showQuiz ? 'border-[#C4A661] text-[#C4A661] bg-[#C4A661]/10' :
                  done ? 'border-[#10B981]/30 text-[#10B981] bg-[#10B981]/5' : 'border-[#262626] text-[#666]'
                }`} data-testid={`lesson-nav-${i}`}>
                {done ? <Check size={12} className="inline mr-1" /> : null}{l.title}
              </button>
            );
          })}
          <button onClick={() => setShowQuiz(true)}
            className={`px-3 py-2 text-xs font-medium whitespace-nowrap border transition-all ${
              showQuiz ? 'border-[#C4A661] text-[#C4A661] bg-[#C4A661]/10' :
              moduleData.quiz_score !== null ? 'border-[#10B981]/30 text-[#10B981]' : 'border-[#262626] text-[#666]'
            }`} data-testid="quiz-nav-btn">
            Quiz {moduleData.quiz_score !== null ? `(${moduleData.quiz_score}/${moduleData.quiz?.length})` : ''}
          </button>
        </div>

        {!showQuiz && lesson ? (
          <div className="bg-[#121212] border border-[#262626] p-6" data-testid="lesson-content">
            <h3 className="text-lg font-bold text-[#F4F0E6] mb-4">{lesson.title}</h3>
            <div className="text-sm text-[#A3A3A3] whitespace-pre-wrap leading-relaxed">{lesson.content}</div>
            <div className="mt-6 pt-4 border-t border-[#1A1A1A] flex justify-between">
              <button disabled={activeLesson === 0} onClick={() => setActiveLesson(activeLesson - 1)} className="text-sm text-[#666] hover:text-[#A3A3A3] disabled:opacity-30"><ArrowLeft size={14} className="inline mr-1" />Previous</button>
              {isCompleted ? (
                <button onClick={() => { if (activeLesson < moduleData.lessons.length - 1) setActiveLesson(activeLesson + 1); else setShowQuiz(true); }} className="text-sm text-[#10B981]"><Check size={14} className="inline mr-1" /> Completed — Next <ArrowRight size={14} className="inline ml-1" /></button>
              ) : (
                <Button onClick={() => completeLesson(lesson.id)} className="btn-gold h-9 px-5 text-sm" data-testid="complete-lesson-btn">Mark Complete <Check size={14} className="ml-1" /></Button>
              )}
            </div>
          </div>
        ) : showQuiz ? (
          <div className="bg-[#121212] border border-[#262626] p-6" data-testid="quiz-section">
            <h3 className="text-lg font-bold text-[#F4F0E6] mb-4">Knowledge Check</h3>
            {quizResult && (
              <div className={`p-4 mb-4 border ${quizResult.passed ? 'border-[#10B981]/30 bg-[#10B981]/5' : 'border-[#EF4444]/30 bg-[#EF4444]/5'}`}>
                <p className={`text-sm font-bold ${quizResult.passed ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>{quizResult.passed ? 'Passed!' : 'Not quite.'} {quizResult.score}/{quizResult.total} correct</p>
              </div>
            )}
            <div className="space-y-6">
              {moduleData.quiz?.map((q, qi) => (
                <div key={qi}>
                  <p className="text-sm text-[#F4F0E6] mb-3">{qi + 1}. {q.q}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => (
                      <button key={oi} onClick={() => { const a = [...quizAnswers]; a[qi] = oi; setQuizAnswers(a); }}
                        className={`w-full text-left px-4 py-2 text-sm border transition-all ${
                          quizAnswers[qi] === oi ? 'border-[#C4A661] text-[#C4A661] bg-[#C4A661]/10' : 'border-[#262626] text-[#A3A3A3] hover:border-[#333]'
                        }`} data-testid={`quiz-${qi}-opt-${oi}`}>{opt}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={submitQuiz} className="btn-gold h-10 px-6 mt-6" data-testid="submit-quiz-btn">Submit Quiz</Button>
          </div>
        ) : null}
      </div>
    );
  }

  // Module list view
  const completedCount = modules.filter(m => m.completed).length;
  return (
    <div data-testid="learning-center">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2"><BookOpen size={20} className="text-[#C4A661]" /> Learning Center</h2>
          <p className="text-sm text-[#A3A3A3]">{completedCount}/{modules.length} courses completed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map(mod => (
          <div key={mod.id} className={`bg-[#121212] border p-5 cursor-pointer hover:border-[#333] transition-all ${mod.completed ? 'border-[#10B981]/30' : 'border-[#262626]'}`} onClick={() => openModule(mod.id)} data-testid={`module-${mod.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${mod.color}20` }}>
                <BookOpen size={20} style={{ color: mod.color }} />
              </div>
              <div>
                <p className="text-sm font-bold text-[#F4F0E6]">{mod.title}</p>
                <p className="text-[10px] text-[#666]">{mod.total_lessons} lessons</p>
              </div>
              {mod.completed && <Check size={16} className="text-[#10B981] ml-auto" />}
            </div>
            <p className="text-xs text-[#666] mb-3">{mod.description}</p>
            <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${mod.total_lessons > 0 ? (mod.completed_lessons / mod.total_lessons) * 100 : 0}%`, backgroundColor: mod.color }} />
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-[#666]">
              <span>{mod.completed_lessons}/{mod.total_lessons} lessons</span>
              {mod.quiz_score !== null && <span>Quiz: {mod.quiz_score}/{mod.quiz_total}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function FreedomPathPage() {
  const { user, getAuthHeader, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const isDark = theme === 'dark';

  const authHeader = getAuthHeader();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, snapRes, profRes] = await Promise.all([
        axios.get(`${API}/api/freedom/dashboard`, { headers: authHeader }),
        axios.get(`${API}/api/freedom/snapshot`, { headers: authHeader }),
        axios.get(`${API}/api/freedom/profile`, { headers: authHeader }),
      ]);
      setDashboard(dashRes.data);
      setSnapshot(snapRes.data);
      setProfile(profRes.data);
    } catch (err) {
      console.error('Freedom Path load error:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchData(); }, []);

  const setupCompleted = dashboard?.setup_completed || profile?.setup_completed;

  // If not set up, show onboarding
  if (!loading && !setupCompleted) {
    return (
      <div className={`min-h-screen transition-colors duration-300 ${t(isDark, 'bg-[#050505] text-[#F4F0E6]', 'bg-[#F5F2EB] text-[#1A1A1A]')}`}>
        {/* Header */}
        <header className={`sticky top-0 z-50 backdrop-blur-md border-b transition-colors ${t(isDark, 'bg-[#0A0A0A]/95 border-[#1A1A1A]', 'bg-white/95 border-[#E5E0D5]')}`}>
          <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link to="/hub" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]" data-testid="logo-link">POWR</Link>
            <div className="flex items-center gap-3">
              <span className={`text-xs hidden sm:block ${t(isDark, 'text-[#A3A3A3]', 'text-[#888]')}`}>Freedom Path</span>
              <button onClick={toggleTheme} className={`w-8 h-8 flex items-center justify-center border transition-all ${t(isDark, 'border-[#333] text-[#A3A3A3] hover:text-[#C4A661]', 'border-[#DDD] text-[#888] hover:text-[#A8893E]')}`} data-testid="theme-toggle">
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button onClick={() => navigate('/hub')} className={`text-sm flex items-center gap-1 ${t(isDark, 'text-[#A3A3A3] hover:text-[#F4F0E6]', 'text-[#888] hover:text-[#1A1A1A]')}`}>
                <ArrowLeft size={16} /> Menu
              </button>
            </div>
          </div>
        </header>

        {/* Onboarding Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#C4A661]/10 to-transparent" />
          <div className="max-w-3xl mx-auto px-4 py-16 text-center relative">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="w-16 h-16 mx-auto mb-6 bg-[#C4A661]/20 flex items-center justify-center">
                <Lightning size={32} className="text-[#C4A661]" weight="fill" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
                Freedom Path<span className="text-[#C4A661]">&#8482;</span>
              </h1>
              <p className="text-[#A3A3A3] text-lg mb-2">Your journey to financial freedom starts here.</p>
              <p className="text-[#666] text-sm max-w-lg mx-auto">
                Set up your financial snapshot to unlock your personalized Freedom Score, 
                debt elimination plan, and progress tracking dashboard.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 pb-16">
          <SnapshotSetup
            onComplete={fetchData}
            existingSnapshot={snapshot && Object.keys(snapshot).length > 0 ? snapshot : null}
            authHeader={authHeader}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${t(isDark, 'bg-[#050505]', 'bg-[#F5F2EB]')}`}>
        <div className="text-[#C4A661] animate-pulse text-lg">Loading Freedom Path...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: ChartLineUp },
    { id: 'coach', label: 'AI Coach', icon: ChatCircle },
    { id: 'elimination', label: 'Debt Plan', icon: Target },
    { id: 'challenges', label: 'Challenges', icon: Lightning },
    { id: 'badges', label: 'Badges', icon: Crown },
    { id: 'income', label: 'Income', icon: TrendUp },
    { id: 'groups', label: 'Groups', icon: Users },
    { id: 'learn', label: 'Learn', icon: BookOpen },
    { id: 'community', label: 'Community', icon: MegaphoneSimple },
    { id: 'wall', label: 'Wins', icon: Trophy },
    ...(dashboard?.faith_mode ? [{ id: 'faith', label: 'Faith', icon: Cross }] : []),
    { id: 'snapshot', label: 'Settings', icon: Gear },
  ];

  const faithQuote = dashboard?.faith_content || (dashboard?.faith_mode ? FAITH_SCRIPTURES[new Date().getDay() % FAITH_SCRIPTURES.length] : null);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${t(isDark, 'bg-[#050505] text-[#F4F0E6]', 'bg-[#F5F2EB] text-[#1A1A1A]')}`} data-testid="freedom-path-page">
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-md border-b transition-colors ${t(isDark, 'bg-[#0A0A0A]/95 border-[#1A1A1A]', 'bg-white/95 border-[#E5E0D5]')}`}>
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/hub" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]" data-testid="logo-link">POWR</Link>
            <span className={t(isDark, 'text-[#333]', 'text-[#DDD]')}>|</span>
            <span className={`text-sm font-medium ${t(isDark, 'text-[#A3A3A3]', 'text-[#888]')}`}>Freedom Path<span className="text-[#C4A661]">&#8482;</span></span>
          </div>
          <div className="flex items-center gap-2">
            {/* Faith mode toggle */}
            <button
              onClick={async () => {
                const newVal = !dashboard?.faith_mode;
                try {
                  await axios.put(`${API}/api/freedom/profile/faith-mode`, { enabled: newVal }, { headers: authHeader });
                  fetchData();
                  toast.success(newVal ? 'Faith Mode enabled' : 'Faith Mode disabled');
                } catch {}
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs transition-all border ${
                dashboard?.faith_mode 
                  ? 'border-[#C4A661] text-[#C4A661] bg-[#C4A661]/10' 
                  : t(isDark, 'border-[#333] text-[#666] hover:text-[#A3A3A3]', 'border-[#DDD] text-[#999] hover:text-[#666]')
              }`}
              data-testid="faith-mode-toggle"
            >
              <Cross size={14} weight={dashboard?.faith_mode ? 'fill' : 'regular'} />
              Faith
            </button>
            <button onClick={toggleTheme} className={`w-8 h-8 flex items-center justify-center border transition-all ${t(isDark, 'border-[#333] text-[#A3A3A3] hover:text-[#C4A661] hover:border-[#C4A661]', 'border-[#DDD] text-[#888] hover:text-[#A8893E] hover:border-[#A8893E]')}`} data-testid="theme-toggle">
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate('/hub')} className={`text-sm flex items-center gap-1 ${t(isDark, 'text-[#A3A3A3] hover:text-[#F4F0E6]', 'text-[#888] hover:text-[#1A1A1A]')}`}>
              <ArrowLeft size={16} /> Menu
            </button>
          </div>
        </div>
      </header>

      {/* Faith Banner */}
      <AnimatePresence>
        {faithQuote && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`border-b transition-colors ${t(isDark, 'bg-[#121212] border-[#1A1A1A]', 'bg-[#F8F6F1] border-[#E5E0D5]')}`}
          >
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 text-center justify-center">
              <Cross size={14} className="text-[#C4A661] shrink-0" weight="fill" />
              <p className={`text-sm italic ${t(isDark, 'text-[#A3A3A3]', 'text-[#666]')}`}>
                &ldquo;{faithQuote.text || faithQuote.ref}&rdquo;
                <span className="text-[#C4A661] ml-2 not-italic text-xs">{faithQuote.ref}</span>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab Navigation */}
      <div className={`border-b transition-colors ${t(isDark, 'border-[#1A1A1A]', 'border-[#E5E0D5]')}`}>
        <div className="max-w-7xl mx-auto px-4 flex gap-0 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#C4A661] text-[#C4A661]'
                  : t(isDark, 'border-transparent text-[#666] hover:text-[#A3A3A3]', 'border-transparent text-[#999] hover:text-[#666]')
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* ─── DASHBOARD TAB ──────────────────────────── */}
          {activeTab === 'dashboard' && dashboard && (
            <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Score + Stats Row */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
                {/* Score Card */}
                <div className="lg:col-span-4 bg-[#121212] border border-[#262626] p-6 flex flex-col items-center">
                  <ScoreRing score={dashboard.freedom_score} />
                  <p className="text-xs text-[#666] mt-3">
                    {dashboard.freedom_score < 300 ? 'Getting Started' :
                     dashboard.freedom_score < 500 ? 'Building Momentum' :
                     dashboard.freedom_score < 700 ? 'Making Progress' : 'Freedom Builder'}
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Debt Remaining" value={`$${dashboard.total_debt.toLocaleString()}`} icon={Warning} color="#EF4444" subtitle={`$${dashboard.debt_eliminated.toLocaleString()} eliminated`} />
                  <StatCard label="Emergency Fund" value={`$${dashboard.emergency_fund.toLocaleString()}`} icon={Shield} color="#10B981" subtitle={`Goal: $${dashboard.emergency_fund_goal.toLocaleString()}`} />
                  <StatCard label="Monthly Income" value={`$${dashboard.total_income.toLocaleString()}`} icon={TrendUp} color="#C4A661" subtitle={`${dashboard.savings_rate}% savings rate`} />
                  <StatCard label="Monthly Surplus" value={`$${dashboard.monthly_surplus.toLocaleString()}`} icon={Wallet} color={dashboard.monthly_surplus >= 0 ? '#10B981' : '#EF4444'} subtitle={`$${dashboard.total_expenses.toLocaleString()} expenses`} />
                </div>
              </div>

              {/* Progress Trackers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Debt Freedom Thermometer */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Target size={16} className="text-[#EF4444]" /> Debt Freedom Thermometer
                  </h3>
                  <Thermometer current={dashboard.total_debt} total={dashboard.original_debt} label="Debt Elimination" color="#EF4444" icon={Warning} />
                  <div className="mt-4 text-center">
                    <span className="text-2xl font-bold text-[#C4A661]">{dashboard.debt_progress}%</span>
                    <span className="text-sm text-[#666] ml-2">toward debt freedom</span>
                  </div>
                </div>

                {/* Emergency Fund */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Shield size={16} className="text-[#10B981]" /> Emergency Fund
                  </h3>
                  <ProgressBar value={dashboard.emergency_fund} max={dashboard.emergency_fund_goal} label="Savings Progress" color="#10B981" />
                  <div className="mt-4 text-center">
                    <span className="text-2xl font-bold text-[#10B981]">{dashboard.ef_progress}%</span>
                    <span className="text-sm text-[#666] ml-2">of goal reached</span>
                  </div>
                </div>
              </div>

              {/* Income + Giving Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Income vs Expenses */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <ChartLineUp size={16} className="text-[#C4A661]" /> Income vs Expenses
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#A3A3A3]">Income</span>
                      <span className="text-sm font-bold text-[#10B981]">${dashboard.total_income.toLocaleString()}</span>
                    </div>
                    <div className="h-4 bg-[#1A1A1A] rounded-full overflow-hidden flex">
                      <div className="h-full bg-[#10B981]" style={{ width: `${dashboard.total_income > 0 ? Math.min(100, (dashboard.total_income / (dashboard.total_income + dashboard.total_expenses)) * 200) : 0}%` }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#A3A3A3]">Expenses</span>
                      <span className="text-sm font-bold text-[#EF4444]">${dashboard.total_expenses.toLocaleString()}</span>
                    </div>
                    <div className="h-4 bg-[#1A1A1A] rounded-full overflow-hidden flex">
                      <div className="h-full bg-[#EF4444]" style={{ width: `${dashboard.total_income > 0 ? Math.min(100, (dashboard.total_expenses / dashboard.total_income) * 100) : 0}%` }} />
                    </div>
                  </div>
                </div>

                {/* Giving Tracker */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <HandHeart size={16} className="text-[#8B5CF6]" /> Giving Tracker
                  </h3>
                  <ProgressBar value={dashboard.total_giving} max={dashboard.total_income * (dashboard.giving_goal_percent / 100)} label={`Giving Goal (${dashboard.giving_goal_percent}% of income)`} color="#8B5CF6" />
                  <div className="mt-4 text-center">
                    <span className="text-2xl font-bold text-[#8B5CF6]">{dashboard.giving_progress}%</span>
                    <span className="text-sm text-[#666] ml-2">of giving goal</span>
                  </div>
                </div>
              </div>

              {/* Debts List */}
              {dashboard.debts?.length > 0 && (
                <div className="bg-[#121212] border border-[#262626] p-6 mb-8">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4">Your Debts</h3>
                  <div className="space-y-3">
                    {dashboard.debts.map((debt, i) => (
                      <div key={debt.id || i} className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-0">
                        <div>
                          <p className="text-sm text-[#F4F0E6]">{debt.name || 'Unnamed Debt'}</p>
                          <p className="text-xs text-[#666]">{DEBT_TYPES.find(t => t.value === debt.type)?.label || debt.type} &bull; {debt.interest_rate}% APR</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${debt.balance <= 0 ? 'text-[#10B981]' : 'text-[#F4F0E6]'}`}>
                            {debt.balance <= 0 ? 'PAID OFF' : `$${debt.balance.toLocaleString()}`}
                          </p>
                          <p className="text-xs text-[#666]">${debt.min_payment}/mo</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => setActiveTab('elimination')}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-2 text-sm text-[#C4A661] hover:text-[#D4B671] border border-[#C4A661]/20 hover:border-[#C4A661]/40 transition-all"
                    data-testid="view-elimination-plan"
                  >
                    View Elimination Plan <ArrowRight size={14} />
                  </button>
                </div>
              )}

              {/* Milestones */}
              {dashboard.milestones?.length > 0 && (
                <div className="bg-[#121212] border border-[#262626] p-6 mb-8">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Trophy size={16} className="text-[#C4A661]" weight="fill" /> Milestones
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {dashboard.milestones.map((m, i) => (
                      <div key={m.id || i} className="bg-[#1A1A1A] border border-[#C4A661]/20 p-3 text-center">
                        <Star size={24} className="text-[#C4A661] mx-auto mb-2" weight="fill" />
                        <p className="text-xs font-bold text-[#F4F0E6]">{m.title}</p>
                        <p className="text-[10px] text-[#666] mt-1">{m.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Payments */}
              {dashboard.recent_payments?.length > 0 && (
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="text-sm font-bold text-[#F4F0E6] uppercase tracking-wider mb-4">Recent Payments</h3>
                  <div className="space-y-2">
                    {dashboard.recent_payments.map((p, i) => (
                      <div key={p.id || i} className="flex items-center justify-between py-2 border-b border-[#1A1A1A] last:border-0">
                        <span className="text-sm text-[#A3A3A3]">{new Date(p.created_at).toLocaleDateString()}</span>
                        <span className="text-sm font-bold text-[#10B981]">-${p.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ─── DEBT ELIMINATION TAB ──────────────────── */}
          {activeTab === 'elimination' && (
            <motion.div key="elimination" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#F4F0E6]">Debt Elimination Engine</h2>
                <p className="text-sm text-[#A3A3A3]">Choose your strategy and track every payment toward freedom.</p>
              </div>
              <DebtElimination authHeader={authHeader} />
            </motion.div>
          )}

          {/* ─── CHALLENGES TAB ─────────────────────────── */}
          {activeTab === 'challenges' && (
            <motion.div key="challenges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Challenges authHeader={authHeader} userId={user?.id} />
            </motion.div>
          )}

          {/* ─── BADGES TAB ────────────────────────────── */}
          {activeTab === 'badges' && (
            <motion.div key="badges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <BadgesView authHeader={authHeader} />
            </motion.div>
          )}

          {/* ─── INCOME TAB ────────────────────────────── */}
          {activeTab === 'income' && (
            <motion.div key="income" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <IncomeCenter authHeader={authHeader} />
            </motion.div>
          )}

          {/* ─── GROUPS TAB ────────────────────────────── */}
          {activeTab === 'groups' && (
            <motion.div key="groups" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AccountabilityGroups authHeader={authHeader} userId={user?.id} />
            </motion.div>
          )}

          {/* ─── LEARN TAB ─────────────────────────────── */}
          {activeTab === 'learn' && (
            <motion.div key="learn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <LearningCenter authHeader={authHeader} />
            </motion.div>
          )}

          {/* ─── EDIT SNAPSHOT TAB ──────────────────────── */}
          {activeTab === 'snapshot' && (
            <motion.div key="snapshot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#F4F0E6]">Edit Financial Snapshot</h2>
                <p className="text-sm text-[#A3A3A3]">Update your numbers as your situation changes.</p>
              </div>
              <SnapshotSetup
                onComplete={() => { fetchData(); setActiveTab('dashboard'); }}
                existingSnapshot={snapshot}
                authHeader={authHeader}
              />
            </motion.div>
          )}

          {/* ─── AI COACH TAB ──────────────────────────── */}
          {activeTab === 'coach' && (
            <motion.div key="coach" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AICoach authHeader={authHeader} userId={user?.id} faithMode={dashboard?.faith_mode} />
            </motion.div>
          )}

          {/* ─── COMMUNITY TAB ─────────────────────────── */}
          {activeTab === 'community' && (
            <motion.div key="community" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CommunityHub authHeader={authHeader} userId={user?.id} />
            </motion.div>
          )}

          {/* ─── SUCCESS WALL TAB ──────────────────────── */}
          {activeTab === 'wall' && (
            <motion.div key="wall" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <SuccessWall authHeader={authHeader} userId={user?.id} />
            </motion.div>
          )}

          {/* ─── FAITH TAB ─────────────────────────────── */}
          {activeTab === 'faith' && dashboard?.faith_mode && (
            <motion.div key="faith" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-[#F4F0E6] flex items-center gap-2">
                  <Cross size={20} className="text-[#C4A661]" weight="fill" />
                  Faith & Stewardship
                </h2>
                <p className="text-sm text-[#A3A3A3]">Biblical financial wisdom for your journey</p>
              </div>
              <FaithContent authHeader={authHeader} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
